import { Tabs, TabsProps, Spin } from '@arco-design/web-react';
import { classnames } from '@extensions/utils/classnames';
import React, { useState, useEffect, useRef } from 'react';
import { t } from '@bay-email/core';
import styles from './index.module.scss';

const { TabPane } = Tabs;

export interface EditTabProps<T extends any = any> extends Omit<TabsProps, 'onChange'> {
  value: Array<T>;
  renderItem: (item: T, index: number) => React.ReactNode;
  onChange: (vals: Array<T>) => any;
  additionItem: T;
  label: string;
  activeTab?: string;
  onActiveTabChange?: (tab: string) => void;
}
export function EditTab<T extends any = any>(props: EditTabProps<T>) {
  const { value, additionItem, activeTab: externalActiveTab, onActiveTabChange } = props;
  const [activeTab, setActiveTab] = useState('0');
  const [addingTab, setAddingTab] = useState(false);
  const addingRef = useRef(false);

  useEffect(() => {
    if (externalActiveTab !== undefined) {
      setActiveTab(externalActiveTab);
    }
  }, [externalActiveTab]);

  const onAddTab = () => {
    if (addingRef.current) return;
    addingRef.current = true;
    setAddingTab(true);
    const safeValue = Array.isArray(value) ? value : [];
    const newTab = safeValue.length.toString();
    setActiveTab(newTab);
    onActiveTabChange?.(newTab);
    props.onChange([...safeValue, additionItem]);
    // Re-enable after the next render cycle
    setTimeout(() => {
      addingRef.current = false;
      setAddingTab(false);
    }, 1000);
  };

  const onDeleteTab = (index: string) => {
    if (index < activeTab) {
      const newTab = (Number(activeTab) - 1).toString();
      setActiveTab(newTab);
      onActiveTabChange?.(newTab);
    }
    if (index === activeTab) {
      const newTab = Number(index) > 0 ? `${Number(index) - 1}` : '0';
      setActiveTab(newTab);
      onActiveTabChange?.(newTab);
    }
    props.onChange(
      (Array.isArray(value) ? value : []).filter((_, vIndex) => Number(index) !== vIndex),
    );
  };

  return (
    <Tabs
      destroyOnHide
      className={classnames(styles.editTab)}
      style={{ border: 'none' }}
      type='card-gutter'
      activeTab={activeTab}
      tabPosition={props.tabPosition}
      editable
      icons={{
        add: addingTab ? <Spin size={12} style={{ display: 'flex' }} /> : undefined,
      }}
      onAddTab={onAddTab}
      onDeleteTab={onDeleteTab}
      onChange={(tab) => { setActiveTab(tab); onActiveTabChange?.(tab); }}
    >
      {(Array.isArray(value) ? value : []).map((item, index) => (
        <TabPane
          title={`${props.label || t('Tab')} ${index + 1}`}
          key={index}
        >
          {props.renderItem(item, index)}
        </TabPane>
      ))}
    </Tabs>
  );
}
